<script setup lang="ts">
import { ref, provide, watch } from 'vue'
import { useRoute } from 'vue-router'
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar'
import DashboardSidebar from '@/components/sidebar/DashboardSidebar.vue'
import DashboardHeader from '@/components/header/DashboardHeader.vue'
import SettingsSidebar from '@/components/sidebar/SettingsSidebar.vue'
import SidebarToggle from '@/components/sidebar/SidebarToggle.vue'
import MobileBottomNav from '@/components/shared/MobileBottomNav.vue'
import { curatedReciterIdSet, DEFAULT_CURATED_RECITER } from '@/data/curatedReciters'
import { useBookmarksSync } from '@/composables/useBookmarksSync'

const settingsOpen = ref(false)
const route = useRoute()

// Keep bookmarks synced to the signed-in Google account (no-op when signed out).
useBookmarksSync()

// Quran state (initialize from saved preferences when available)
const savedQuranStateRaw = localStorage.getItem('quranState')
let savedQuranState: any = null
try {
  savedQuranState = savedQuranStateRaw ? JSON.parse(savedQuranStateRaw) : null
} catch {}

if (savedQuranState && typeof savedQuranState === 'object') {
  delete savedQuranState.readingMode
  delete savedQuranState.audioBitrate
  delete savedQuranState.showTajweed
  delete savedQuranState.pageSearchQuery
  if (savedQuranState.navigationMode === 'page') {
    savedQuranState.navigationMode = 'surah'
  }
}

const quranState = ref({
  navigationMode: 'surah' as 'surah' | 'verse' | 'juz',
  selectedFont: 'tajweed',
  selectedReciter: DEFAULT_CURATED_RECITER,
  showTransliteration: true,
  showTranslation: true,
  showAudio: true,
  // Selection state for navigation
  selectedSurah: 1,
  selectedVerse: 1,
  selectedJuz: 1,
  selectedPage: 1,
  // Search states
  surahSearchQuery: '',
  verseSurahSearchQuery: '',
  verseNumber: 1,
  juzSearchQuery: '',
  ...(savedQuranState || {}),
})

const allowedFonts = ['indopak', 'tajweed'] as const
if (!allowedFonts.includes(quranState.value.selectedFont as (typeof allowedFonts)[number])) {
  quranState.value.selectedFont = 'tajweed'
}
if (!curatedReciterIdSet.has(String(quranState.value.selectedReciter))) {
  quranState.value.selectedReciter = DEFAULT_CURATED_RECITER
}

// Persist display options and related preferences
watch(quranState, (val) => {
  try {
    localStorage.setItem('quranState', JSON.stringify(val))
  } catch {}
}, { deep: true })

// Provide state to child components
provide('quranState', quranState)

const updateQuranState = (key: string, value: any) => {
  (quranState.value as any)[key] = value
}

watch(
  () => route.query,
  (query) => {
    if (route.name !== 'quran') return

    const mode = String(query.mode || '')
    const page = Number(query.page || 0)
    const surah = Number(query.surah || 0)
    const ayah = Number(query.ayah || 0)

    if (page > 0 && page <= 604) {
      quranState.value.selectedPage = page
    }
    if (mode === 'verse') {
      quranState.value.navigationMode = 'verse'
      if (surah > 0 && surah <= 114) quranState.value.selectedSurah = surah
      if (ayah > 0) quranState.value.selectedVerse = ayah
    } else if (mode === 'page') {
      quranState.value.navigationMode = 'surah'
    }
  },
  { immediate: true },
)

const handleNavigateTo = (data: { mode: 'surah' | 'verse' | 'juz'; value: number; surah?: number; verse?: number }) => {
  // This will emit to QuranView to update the display
  quranState.value.navigationMode = data.mode
  if (data.mode === 'surah') {
    quranState.value.selectedSurah = data.value
  } else if (data.mode === 'verse') {
    quranState.value.selectedSurah = data.surah || data.value
    quranState.value.selectedVerse = data.verse || 1
  } else if (data.mode === 'juz') {
    quranState.value.selectedJuz = data.value
  }
}
</script>

<template>
  <div class="flex h-dvh max-h-dvh min-h-0 flex-col overflow-hidden bg-background text-foreground">
    <!-- Header at the very top -->
    <DashboardHeader @open-settings="settingsOpen = true" />

    <!-- Sidebar and Content Layout -->
    <SidebarProvider
      class="flex !min-h-0 flex-1 overflow-hidden"
      :default-open="true"
      :style="{
        '--sidebar-width': '20rem',
        /* Wider icon rail so Surah / Juz pills + stacked icons aren’t cramped */
        '--sidebar-width-icon': '6.25rem',
      }"
    >
      <DashboardSidebar
        :quran-state="quranState"
        @update:navigationMode="updateQuranState('navigationMode', $event)"
        @update:selectedSurah="updateQuranState('selectedSurah', $event)"
        @update:selectedVerse="updateQuranState('selectedVerse', $event)"
        @update:selectedJuz="updateQuranState('selectedJuz', $event)"
        @update:selectedPage="updateQuranState('selectedPage', $event)"
        @update:surahSearchQuery="updateQuranState('surahSearchQuery', $event)"
        @update:verseSurahSearchQuery="updateQuranState('verseSurahSearchQuery', $event)"
        @update:verseNumber="updateQuranState('verseNumber', $event)"
        @update:juzSearchQuery="updateQuranState('juzSearchQuery', $event)"
        @navigate-to="handleNavigateTo($event)"
      />
      
      <SidebarInset class="flex !min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <!-- Single scroll region so sticky is relative to this viewport (toggle stays visible while reading) -->
        <div
          id="dashboard-inset-scroll"
          tabindex="0"
          role="region"
          aria-label="Main content"
          class="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <div
            class="sticky top-0 z-50 flex shrink-0 items-center justify-start border-b bg-background/90 px-2 py-1 backdrop-blur supports-[backdrop-filter]:bg-background/60 sm:px-4"
          >
            <SidebarToggle />
          </div>
          <div class="min-h-0 min-w-0 flex-1 pb-20 md:pb-0">
            <slot />
          </div>
        </div>
      </SidebarInset>

      <!-- Settings Sidebar (Right) -->
      <SettingsSidebar
        :open="settingsOpen"
        :quran-state="quranState"
        @update:open="settingsOpen = $event"
        @update:selectedFont="updateQuranState('selectedFont', $event)"
        @update:selectedReciter="updateQuranState('selectedReciter', $event)"
        @update:showTransliteration="updateQuranState('showTransliteration', $event)"
        @update:showTranslation="updateQuranState('showTranslation', $event)"
        @update:showAudio="updateQuranState('showAudio', $event)"
      />
    </SidebarProvider>

    <MobileBottomNav />
  </div>
</template>
